import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';

const PLATFORM_TYPES = ['amazon', 'flipkart', 'meesho'];

const Platforms = () => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ marketplace_type: 'amazon', account_name: '', api_credentials: '{}', status: 'disconnected' });

  const fetch = async () => {
    try {
      setLoading(true);
      const res = await api.get('/platforms/list.php');
      if (res.data.success) setPlatforms(res.data.data || []);
      else setError(res.data.message);
    } catch (e) { setError('Failed to load platforms'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ marketplace_type: 'amazon', account_name: '', api_credentials: '{}', status: 'disconnected' });
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    const creds = typeof p.api_credentials === 'string' ? p.api_credentials : JSON.stringify(p.api_credentials || {});
    setForm({ marketplace_type: p.marketplace_type, account_name: p.account_name, api_credentials: creds, status: p.status });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, api_credentials: form.api_credentials };
      if (editing) {
        await api.post('/platforms/update.php', { id: editing.id, ...payload });
      } else {
        await api.post('/platforms/create.php', payload);
      }
      setShowForm(false);
      fetch();
    } catch (e) { setError('Failed to save platform'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this platform?')) return;
    try {
      await api.post('/platforms/delete.php', { id });
      fetch();
    } catch (e) { setError('Failed to delete platform'); }
  };

  const typeColors = { amazon: 'bg-orange-100 text-orange-800', flipkart: 'bg-blue-100 text-blue-800', meesho: 'bg-purple-100 text-purple-800' };

  if (loading) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Platforms</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage marketplace platforms: {platforms.length} registered</p>
        </div>
        <button onClick={openCreate} className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">Add Platform</button>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {platforms.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">No platforms added. Click "Add Platform" to create one.</div>
        ) : platforms.map(p => (
          <div key={p.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${typeColors[p.marketplace_type] || 'bg-gray-100 text-gray-800'}`}>{p.marketplace_type}</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${p.status === 'connected' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{p.status}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{p.account_name}</h3>
            {p.last_sync && <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Last sync: {new Date(p.last_sync).toLocaleString()}</p>}
            <div className="flex gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button onClick={() => openEdit(p)} className="text-sm text-primary-600 hover:text-primary-900 dark:hover:text-primary-400">Edit</button>
              <button onClick={() => handleDelete(p.id)} className="text-sm text-red-600 hover:text-red-900 dark:hover:text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Platform' : 'Add Platform'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform Type *</label>
              <select value={form.marketplace_type} onChange={e => setForm({...form, marketplace_type: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" required>
                {PLATFORM_TYPES.map(t => <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Name *</label>
              <input type="text" value={form.account_name} onChange={e => setForm({...form, account_name: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="connected">Connected</option>
                <option value="disconnected">Disconnected</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Credentials (JSON)</label>
            <textarea value={form.api_credentials} onChange={e => setForm({...form, api_credentials: e.target.value})} rows="4" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white font-mono text-sm"></textarea>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors">{editing ? 'Update' : 'Create'} Platform</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Platforms;
